import { useState, useEffect, useMemo, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { BridgeBadge } from '@/components/ui/BridgeBadge'
import { requireClient } from '@/lib/matrix/client'
import { startDirectMessage } from '@/lib/matrix/dm'
import { availableBridges, startBridgedChat, type BridgeNetwork } from '@/lib/matrix/bridges'
import { useNavigate } from 'react-router'
import { useRoomStore } from '@/lib/store/room-store'

interface NewDirectMessageDialogProps {
  onClose: () => void
}

interface UserResult {
  userId: string
  displayName?: string
  avatarUrl?: string | null
}

const MXID_RE = /^@[^:]+:.+/

export function NewDirectMessageDialog({ onClose }: NewDirectMessageDialogProps) {
  // null = Matrix (user search); otherwise we're starting a chat via that bridge.
  const [bridge, setBridge] = useState<BridgeNetwork | null>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserResult[]>([])
  const [searching, setSearching] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { setActiveRoom } = useRoomStore()

  const bridges = useMemo(() => availableBridges(requireClient()), [])

  useEffect(() => {
    if (bridge) return // user-directory search only applies to Matrix mode
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const client = requireClient()
        const res = await client.searchUserDirectory({ term, limit: 10 })
        if (cancelled) return
        setResults(
          res.results.map((r) => ({
            userId: r.user_id,
            displayName: r.display_name,
            avatarUrl: r.avatar_url ?? null,
          }))
        )
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [query, bridge])

  const openInRoom = (roomId: string) => {
    setActiveRoom(roomId)
    navigate(`/room/${encodeURIComponent(roomId)}`)
    onClose()
  }

  const openDirectMessage = async (userId: string) => {
    if (starting) return
    setStarting(true)
    setError(null)
    try {
      openInRoom(await startDirectMessage(requireClient(), userId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start direct message')
      setStarting(false)
    }
  }

  const startBridge = async (network: BridgeNetwork, identifier: string) => {
    if (starting) return
    setStarting(true)
    setError(null)
    try {
      // Sends the command and drops us in the management room to watch the
      // bridge create the portal (which then appears in the room list).
      openInRoom(await startBridgedChat(requireClient(), network, identifier))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start chat')
      setStarting(false)
    }
  }

  const switchTo = (b: BridgeNetwork | null) => {
    setBridge(b)
    setQuery('')
    setResults([])
    setError(null)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const term = query.trim()
    if (bridge) {
      if (term) startBridge(bridge, term)
      return
    }
    if (results[0]) {
      openDirectMessage(results[0].userId)
    } else if (MXID_RE.test(term)) {
      openDirectMessage(term)
    } else {
      setError('Enter a full user ID (e.g. @alice:example.org) or pick a result')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))] mb-4">
          {bridge ? `New ${bridge.label} chat` : 'New Direct Message'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            label={bridge ? bridge.label : 'To'}
            placeholder={bridge ? bridge.placeholder : 'Name or @user:server'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={starting}
          />

          {bridge ? (
            <p className="text-xs text-[rgb(var(--np-tx-muted))] px-1">
              We'll ask the {bridge.label} bridge to open this chat. It appears in your
              room list once the bridge connects it.
            </p>
          ) : (
            <div className="min-h-[3rem] max-h-64 overflow-y-auto -mx-1">
              {searching && results.length === 0 && (
                <div className="flex items-center justify-center py-4">
                  <Spinner size={18} />
                </div>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="text-xs text-[rgb(var(--np-tx-muted))] px-2 py-3">No users found</p>
              )}
              {results.map((user) => (
                <button
                  key={user.userId}
                  type="button"
                  onClick={() => openDirectMessage(user.userId)}
                  disabled={starting}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-[rgb(var(--np-raised))] transition-colors disabled:opacity-50"
                >
                  <Avatar mxc={user.avatarUrl} name={user.displayName || user.userId} userId={user.userId} size={32} />
                  <div className="min-w-0">
                    <div className="text-sm text-[rgb(var(--np-tx-primary))] truncate">
                      {user.displayName || user.userId}
                    </div>
                    {user.displayName && (
                      <div className="text-xs text-[rgb(var(--np-tx-muted))] truncate">{user.userId}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Source switcher: Matrix DM vs. connected bridges. */}
          {(bridges.length > 0 || bridge) && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span className="text-xs text-[rgb(var(--np-tx-muted))]">Via:</span>
              <SourceChip active={!bridge} onClick={() => switchTo(null)}>
                Matrix
              </SourceChip>
              {bridges.map((b) => (
                <SourceChip key={b.type} active={bridge?.type === b.type} onClick={() => switchTo(b)}>
                  <BridgeBadge type={b.type} size={12} />
                  {b.label}
                </SourceChip>
              ))}
            </div>
          )}

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose} disabled={starting}>
              Cancel
            </Button>
            <Button type="submit" disabled={starting || query.trim().length === 0}>
              {starting ? <Spinner size={16} /> : bridge ? 'Start chat' : 'Message'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SourceChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'border-[rgb(var(--np-accent))] text-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.1)]'
          : 'border-[rgb(var(--np-border))] text-[rgb(var(--np-tx-secondary))] hover:bg-[rgb(var(--np-raised))]'
      }`}
    >
      {children}
    </button>
  )
}
